import { urlFixer } from 'pano-utils';
import {
  type GetProps,
  LinkTarget,
  type ModalSize,
  type ModalSizeType,
  type OpenStyle,
} from '@types';
import { LINK_LENGTH } from '@constants';
import { sendWarning } from '@components/Noti';
import Textarea from '@components/Textarea';
import { Dropdown } from '@components/Dropdown';
import { Validator } from '@components/Validator';
import FeatureToggle from '@components/FeatureToggle';
import {
  defaultModalSize,
  linkTargetItems,
  modalSizeTypeItems,
  openStyleItems,
} from '../constants';
import ModalSizeSetting from './ModalSizeSetting';

type Props = Pick<GetProps<typeof Textarea>, 'value'> & {
  className?: string;
  linkTarget?: LinkTarget;
  isRequired?: boolean;
  isMusicToggleOn?: boolean;
  modalSizeType?: ModalSizeType;
  size?: ModalSize;
  openStyle?: OpenStyle;
  onChange: (val: string) => void;
  onTargetChange?: (k: LinkTarget) => void;
  onMusicToggleChange?: (bool: boolean) => void;
  onModalSizeTypeChange?: (k: ModalSizeType) => void;
  onModalSizeChange?: (k: ModalSize) => void;
  onOpenStyleChange?: (val: OpenStyle) => void;
};

function LinkSetting({
  className,
  value,
  linkTarget = LinkTarget.blank,
  isRequired,
  isMusicToggleOn = false,
  modalSizeType = 0,
  size,
  openStyle = 1,
  onChange,
  onTargetChange,
  onMusicToggleChange,
  onModalSizeTypeChange,
  onModalSizeChange,
  onOpenStyleChange,
}: Props) {
  const modalSize = size ?? defaultModalSize;
  const validFn = (str: string) => {
    if (!str && isRequired) {
      sendWarning('请输入链接地址');
      return false;
    }
    return true;
  };

  return (
    <div className={className}>
      <Validator value={value} validFn={validFn}>
        {({ value: innerValue, warning }) => (
          <Textarea
            scheme="dark"
            className="h-115"
            placeholder="请输入链接地址"
            maxLength={LINK_LENGTH}
            value={innerValue}
            warning={warning}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            onBlur={(e) => {
              onChange(urlFixer(e.target.value.trim()));
            }}
          />
        )}
      </Validator>
      {!!onTargetChange && (
        <>
          <div className="mt-20 mb-10">链接打开方式：</div>
          <Dropdown
            items={linkTargetItems}
            onChange={(id) => onTargetChange(id)}
            selected={linkTarget}
          />
        </>
      )}
      {linkTarget === LinkTarget.modal && (
        <div className="mt-10 text-sm text-orange">
          注：弹出层打开网址需要https开头，且没有iframe限制
        </div>
      )}
      {!!onMusicToggleChange && linkTarget === LinkTarget.modal && (
        <FeatureToggle
          title="打开时关闭背景音乐"
          isOn={isMusicToggleOn}
          onToggle={(bool) => onMusicToggleChange(bool)}
        />
      )}
      {linkTarget === LinkTarget.modal && !!onOpenStyleChange && (
        <>
          <div className="mt-20 mb-10">弹出层样式：</div>
          <Dropdown
            items={openStyleItems}
            onChange={(typeId) => {
              onOpenStyleChange(typeId);
            }}
            selected={openStyle}
          />
        </>
      )}
      {linkTarget === LinkTarget.modal && !!onModalSizeTypeChange && (
        <>
          <div className="mt-20 mb-10">弹出层尺寸：</div>
          <Dropdown
            items={modalSizeTypeItems}
            onChange={(id) => {
              onModalSizeTypeChange(id);
              if (id === 1 && onModalSizeChange) {
                onModalSizeChange(defaultModalSize);
              }
            }}
            selected={modalSizeType}
          />
        </>
      )}
      {linkTarget === LinkTarget.modal &&
        !!onModalSizeChange &&
        modalSizeType === 1 && (
          <ModalSizeSetting
            currentModalSize={modalSize}
            onChange={onModalSizeChange}
          />
        )}
    </div>
  );
}

export default LinkSetting;
